/**
 * Aviso preso ao formulário (credencial inválida, e-mail já usado, 500).
 *
 * Não é o ErrorState do design system: aquele é para um bloco que falhou ao
 * CARREGAR. Aqui o conteúdo está na tela e o que falhou foi o envio — o aviso
 * precisa ficar junto do botão, não substituir o formulário.
 *
 * `role="alert"` faz o leitor de tela anunciar sem precisar mover o foco.
 */
import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * `warning` é o aviso que não impede a ação, mas muda o que ela significa: "salvar isto
 * tira o grupo do rodízio". `danger` é o que já falhou; `info`, o que é só contexto.
 */
export type FormAlertTone = 'danger' | 'warning' | 'success' | 'info';

export interface FormAlertProps {
  tone?: FormAlertTone;
  /** Linha em negrito antes do texto, quando o aviso precisa de um nome. */
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}

// Fundo suave do tom + texto em tinta normal: o tom vive no fundo e no ícone,
// e o texto continua legível (≥ 4,5:1) nos dois temas.
const TONE_STYLE: Record<FormAlertTone, string> = {
  danger: 'bg-danger-soft',
  warning: 'bg-warning-soft',
  success: 'bg-success-soft',
  info: 'bg-info-soft',
};

const TONE_ICON_COLOR: Record<FormAlertTone, string> = {
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-info',
};

const TONE_ICON = {
  danger: AlertCircle,
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
} as const;

export function FormAlert({ tone = 'danger', title, children, className }: FormAlertProps) {
  const Icon = TONE_ICON[tone];

  return (
    <div
      role="alert"
      data-tone={tone}
      className={cn(
        'flex items-start gap-2.5 rounded-md px-3.5 py-3 text-sm leading-normal text-foreground',
        TONE_STYLE[tone],
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', TONE_ICON_COLOR[tone])} />
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}
